import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Typography, Segmented, Collapse } from "antd";
import { BaseFilterItem } from "components/UI/BaseFilter/BaseFilterItem/BaseFilterItem.tsx";
import { useFilterEditorContext } from "components/UI/BaseFilter/FilterEditorContext.tsx";
import { FilterGroup as FilterGroupType } from "components/UI/BaseFilter/types/filterEditor";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";

interface FilterGroupProps {
    group: FilterGroupType;
}

export const FilterGroup = ({ group }: FilterGroupProps) => {
    const { t } = usePaldockTranslation("ui.offer-filter");
    const { dispatch, context } = useFilterEditorContext();

    const items = [
        {
            key: group.id,
            label: (
                <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                    <Col>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            {t("filter.group.name")}
                        </Typography.Title>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                size="small"
                                type={"link"}
                                icon={<PlusOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch({ type: "addRuleToGroup", groupId: group.id, context });
                                }}
                            >
                                {t("actions.add_filter")}
                            </Button>
                            <Button
                                size="small"
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    dispatch({ type: "remove", id: group.id });
                                }}
                                aria-label="Remove filter group"
                            />
                        </Space>
                    </Col>
                </Row>
            ),
            children: (
                <div>
                    {group.rules.length > 0 ? (
                        <div>
                            {group.rules.map((rule, index) => (
                                <div key={rule.id}>
                                    <div className="mb-2">
                                        <BaseFilterItem rule={rule} />
                                    </div>
                                    {/* Show logical operator between filters within group (not after the last one) */}
                                    {index < group.rules.length - 1 && (
                                        <div className="flex justify-center my-4">
                                            <Segmented
                                                value={group.operator}
                                                onChange={(value) =>
                                                    dispatch({
                                                        type: "updateGroup",
                                                        id: group.id,
                                                        operator: value as "and" | "or",
                                                    })
                                                }
                                                options={[
                                                    { label: t("labels.and"), value: "and" },
                                                    { label: t("labels.or"), value: "or" },
                                                ]}
                                                style={{ width: "250px" }}
                                                className="[&_.ant-segmented-item]:!w-[125px]"
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-gray-500 py-4">
                            <Typography.Text type="secondary">{t("actions.click_to_add_filter")}</Typography.Text>
                        </div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <Card className="rounded-lg w-full border-none bg-gray-200">
            <Collapse
                items={items}
                defaultActiveKey={[group.id]}
                ghost
                expandIconPosition="start"
                className="[&_.ant-collapse-header]:!p-0 [&_.ant-collapse-header]:!pb-4"
            />
        </Card>
    );
};
